import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Mars, Venus } from "lucide-react";

const EMPTY = { name: "", group_id: "", gender: "male", phone1: "", phone2: "" };

export default function StudentFormDialog({ open, onOpenChange, groups, student, presetGroupId, onSaved }) {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const editing = !!student;

  useEffect(() => {
    if (open) {
      setError("");
      if (student) {
        setForm({ name: student.name, group_id: student.group_id || "", gender: student.gender || "male", phone1: student.phone1 || "", phone2: student.phone2 || "" });
      } else {
        setForm({ ...EMPTY, group_id: presetGroupId || groups[0]?.id || "" });
      }
    }
  }, [open, student, presetGroupId, groups]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      let res;
      if (editing) res = await api.put(`/students/${student.id}`, form);
      else res = await api.post("/students", form);
      toast.success(editing ? t("attendance.saved") : t("attendance.studentAdded"));
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="student-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? t("student.editTitle") : t("student.newTitle")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>{t("student.name")}</Label>
            <Input value={form.name} onChange={(e) => patch("name", e.target.value)} data-testid="student-name-input" required autoFocus />
          </div>
          <div className="space-y-2">
            <Label>{t("student.group")}</Label>
            <Select value={form.group_id} onValueChange={(v) => patch("group_id", v)}>
              <SelectTrigger data-testid="student-group-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("student.gender")}</Label>
            <div className="flex gap-2">
              <button type="button" onClick={() => patch("gender", "male")} data-testid="gender-male"
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg border py-2 text-sm font-medium transition-colors ${form.gender === "male" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                <Mars className="h-4 w-4" /> {t("student.male")}
              </button>
              <button type="button" onClick={() => patch("gender", "female")} data-testid="gender-female"
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg border py-2 text-sm font-medium transition-colors ${form.gender === "female" ? "border-pink-500 bg-pink-50 text-pink-700" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                <Venus className="h-4 w-4" /> {t("student.female")}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{t("student.phone1")}</Label>
              <Input value={form.phone1} onChange={(e) => patch("phone1", e.target.value)} data-testid="student-phone1" dir="ltr" />
            </div>
            <div className="space-y-2">
              <Label>{t("student.phone2")}</Label>
              <Input value={form.phone2} onChange={(e) => patch("phone2", e.target.value)} data-testid="student-phone2" dir="ltr" />
            </div>
          </div>
          {error && <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="student-error">{error}</div>}
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-secondary">{t("student.cancel")}</button>
            <button type="submit" disabled={saving} data-testid="student-save" className="brand-btn rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60">{t("student.save")}</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
